// src/services/api.js
// All API calls for Delefy.
// s_o backend: port 8000 (auth)
// Delefy backend: port 8001 (processing)
//
// Task 3 (cookie auth):
//   REACT_APP_COOKIE_AUTH controls whether login/register store a token
//   at all. Defaults to true (Deploy) — set to "false" in .env.dev to
//   keep legacy localStorage/Bearer-header behavior on Dev.
//   authHeader() self-adapts: if no token is stored (cookie mode), it
//   sends no Authorization header at all — the so_session cookie
//   (sent via credentials: 'include' on every call) carries auth instead.
//
// OTP feature additions:
//   apiRegister now returns {pending_id} instead of a token — signup is
//   a two-step flow, apiRegisterVerify completes it.
//   apiLogin can return status:"restore_available" (not an error) when
//   credentials are correct but the account is Pending_Deletion.

const SO_URL = process.env.REACT_APP_SO_URL;
const CA_URL = process.env.REACT_APP_CA_URL;
const APP_ID = 'APP_CAAPP_20260508_224158_4252';

const COOKIE_AUTH = process.env.REACT_APP_COOKIE_AUTH !== 'false';

// ── Token helpers ─────────────────────────────────────────────────────────────

export const getToken = () =>
  localStorage.getItem('ca_token') || sessionStorage.getItem('ca_token');

export const setToken = (token, persist) => {
  if (persist) localStorage.setItem('ca_token', token);
  else sessionStorage.setItem('ca_token', token);
};

export const clearToken = () => {
  localStorage.removeItem('ca_token');
  localStorage.removeItem('ca_user');
  sessionStorage.removeItem('ca_token');
  sessionStorage.removeItem('ca_user');
};

const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Shared error-shape helper — used across every OTP-related call since
// the backend's error detail can be a plain string, a Pydantic array,
// or a structured dict ({message, attempts_left, locked, wait_seconds}).
const _parseErrorDetail = (data, fallback) => {
  const detail = data.detail;
  if (typeof detail === 'string') return { message: detail };
  if (Array.isArray(detail)) return { message: detail.map(d => d.msg).join(', ') };
  if (detail && typeof detail === 'object') return detail;
  return { message: fallback };
};

// ── Auth: Login ────────────────────────────────────────────────────────────

export const apiLogin = async (username, password) => {
  const res = await fetch(`${SO_URL}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Login failed');
    const err = new Error(parsed.message);
    err.attemptsLeft = parsed.attempts_left;
    err.locked = parsed.locked;
    err.retryAfterSeconds = parsed.retry_after_seconds;
    throw err;
  }
  // data.status is either "success" (data.user/data.token present) or
  // "restore_available" (data.restore_token present, no user/token yet) —
  // both are 200 OK, caller (AuthModal) branches on data.status.
  return data;
};

export const apiLoginRestoreConfirm = async (restoreToken, confirm) => {
  const res = await fetch(`${SO_URL}/auth/login/restore-confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restore_token: restoreToken, confirm }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Restore failed');
    throw new Error(parsed.message);
  }
  return data;
};

// ── Auth: Signup (two-step, OTP-verified) ──────────────────────────────────

export const apiRegister = async ({ username, password, full_name, email }) => {
  const res = await fetch(`${SO_URL}/auth/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, full_name, email, app_id: APP_ID }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Registration failed');
    throw new Error(parsed.message);
  }
  // { status: "success", pending_id, message }
  return data;
};

export const apiRegisterVerify = async (pendingId, otp) => {
  const res = await fetch(`${SO_URL}/auth/register/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pending_id: pendingId, otp }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Verification failed');
    const err = new Error(parsed.message);
    err.attemptsLeft = parsed.attempts_left;
    err.locked = parsed.locked;
    err.expired = parsed.expired;
    throw err;
  }
  return data;
};

export const apiRegisterResend = async (pendingId) => {
  const res = await fetch(`${SO_URL}/auth/register/resend`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pending_id: pendingId }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Could not resend code');
    const err = new Error(parsed.message);
    err.waitSeconds = parsed.wait_seconds;
    throw err;
  }
  return data;
};

// ── Auth: Forgot Password ────────────────────────────────────────────────

export const apiForgotPasswordRequest = async (email, username) => {
  const res = await fetch(`${SO_URL}/auth/forgot-password/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, username, app_id: APP_ID }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Request failed');
    const err = new Error(parsed.message);
    err.waitSeconds = parsed.wait_seconds;
    throw err;
  }
  return data;
};

export const apiForgotPasswordConfirm = async (email, otp, newPassword, confirmPassword) => {
  const res = await fetch(`${SO_URL}/auth/forgot-password/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp, new_password: newPassword, confirm_password: confirmPassword }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Reset failed');
    const err = new Error(parsed.message);
    err.attemptsLeft = parsed.attempts_left;
    err.locked = parsed.locked;
    throw err;
  }
  return data;
};

// ── Auth: Me / Logout ────────────────────────────────────────────────────

export const apiMe = async () => {
  const res = await fetch(`${SO_URL}/auth/me`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return null;
  return res.json();
};

export const apiLogout = async () => {
  await fetch(`${SO_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
  });
  clearToken();
};

// ── Master XML ────────────────────────────────────────────────────────────────

export const apiMasterXmlStatus = async () => {
  const res = await fetch(`${CA_URL}/ca/setup/master-xml/status`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return { uploaded: false, ledger_count: 0, indicator: 'red' };
  return res.json();
};

export const apiUploadMasterXml = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${CA_URL}/ca/setup/master-xml`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: form,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Upload failed');
  return data;
};

export const apiDeleteMasterXml = async () => {
  const res = await fetch(`${CA_URL}/ca/setup/master-xml`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return null;
  return res.json();
};

export const apiSearchLedgers = async (query = '') => {
  const params = query ? `?q=${encodeURIComponent(query)}` : '';
  const res = await fetch(`${CA_URL}/ca/ledgers${params}`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return { ledgers: [] };
  return res.json();
};

// ── Processing ────────────────────────────────────────────────────────────────

export const apiCheckBankPassword = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${CA_URL}/ca/check-bank-password`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: form,
  });
  if (!res.ok) return { password_required: false };
  return res.json();
};

export const apiProcess = async (billFiles, bankFile, bankPassword = null) => {
  const form = new FormData();
  billFiles.forEach(f => form.append('bills', f));
  if (bankFile) form.append('bank_statement', bankFile);
  if (bankPassword) form.append('bank_password', bankPassword);
  const res = await fetch(`${CA_URL}/ca/process`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: form,
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(
      typeof data.detail === 'string' ? data.detail : 'Processing failed'
    );
    err.detail = data.detail;
    throw err;
  }
  return data;
};

export const apiJobStatus = async (jobId) => {
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/status`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return null;
  return res.json();
};

// ── Review ────────────────────────────────────────────────────────────────────

export const apiReviewBills = async (jobId) => {
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/review/bills`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return { bills: [] };
  return res.json();
};

export const apiReviewBank = async (jobId) => {
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/review/bank`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return { transactions: [] };
  return res.json();
};

// ── Confirm ───────────────────────────────────────────────────────────────────

export const apiConfirmBills = async (jobId, confirmed, corrections) => {
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/confirm/bills`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmed, corrections }),
  });
  if (!res.ok) throw new Error('Confirm failed');
  return res.json();
};

export const apiConfirmBank = async (jobId, links, corrections = {}) => {
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/confirm/bank`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ links, corrections }),
  });
  if (!res.ok) throw new Error('Confirm failed');
  return res.json();
};

// ── Export ────────────────────────────────────────────────────────────────────

export const apiExportBills = async (jobId, entryMode = 'accounting', aggregated = false) => {
  const params = new URLSearchParams({ entry_mode: entryMode, aggregated });
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/export/bills?${params}`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail?.message || 'Export failed');
    err.detail = body.detail;
    throw err;
  }
  return res.blob();
};

export const apiExportBank = async (jobId) => {
  const res = await fetch(`${CA_URL}/ca/job/${jobId}/export/bank`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail?.message || 'Export failed');
    err.detail = body.detail;
    throw err;
  }
  return res.blob();
};

// ── Calibration kit download ──────────────────────────────────────────────────
// Static TSF files bundled in public folder
export const downloadCalibrationKit = () => {
  const a = document.createElement('a');
  a.href = '/ca_app_calibration_kit.zip';
  a.download = 'CA_App_Calibration_Kit.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// ── Waitlist ───────────────────────────────────────────────────────────────────

export const apiJoinWaitlist = async () => {
  const res = await fetch(`${CA_URL}/ca/waitlist`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Failed to join waitlist');
  return data;
};

export const apiGetUsage = async () => {
  const res = await fetch(`${SO_URL}/usage/${APP_ID}`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return { today: 0, this_month: 0 };
  const data = await res.json();
  return data.usage || { today: 0, this_month: 0 };
};

// ── Feedback ──────────────────────────────────────────────────────────────────

export const apiSendFeedback = async (payload) => {
  const res = await fetch(`${CA_URL}/ca/feedback`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Feedback submission failed');
  return res.json();
};

// ── Bill Credits ──────────────────────────────────────────────────────────────────

export const apiGetCredits = async () => {
  const res = await fetch(`${CA_URL}/ca/credits`, {
    credentials: 'include',
    headers: authHeader(),
  });
  if (!res.ok) return { credits_available: 0 };
  return res.json();
};

// Add this to api.js — called from handleReset when jobId exists

export const apiCancelJob = async (jobId) => {
  try {
    const res = await fetch(`${CA_URL}/ca/job/${jobId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: authHeader(),
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
};

// ── Profile & Payment ──────────────────────────────────────────────────────────────────

export const apiGetProfile = async () => {
  const res = await fetch(`${SO_URL}/profile`, { credentials: 'include', headers: authHeader() });
  if (!res.ok) return null;
  const data = await res.json();
  return data.profile;
};

export const apiUpdateProfile = async (profileData) => {
  const res = await fetch(`${SO_URL}/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(profileData),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Profile update failed');
    throw new Error(parsed.message);
  }
  return data;
};

// ── Profile: Email Change (OTP-verified) ─────────────────────────────────

export const apiRequestEmailChange = async (newEmail) => {
  const res = await fetch(`${SO_URL}/profile/email/request-change`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_email: newEmail, app_id: APP_ID }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Could not send verification code');
    const err = new Error(parsed.message);
    err.waitSeconds = parsed.wait_seconds;
    throw err;
  }
  return data;
};

export const apiConfirmEmailChange = async (otp) => {
  const res = await fetch(`${SO_URL}/profile/email/confirm-change`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ otp }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Verification failed');
    const err = new Error(parsed.message);
    err.attemptsLeft = parsed.attempts_left;
    err.locked = parsed.locked;
    throw err;
  }
  return data;
};

export const apiCreateOrder = async (quantity) => {
  const form = new FormData();
  form.append('quantity', quantity);
  const res = await fetch(`${CA_URL}/ca/purchase/create-order`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeader(),
    body: form,
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.detail?.message || 'Order creation failed');
    err.missing_fields = data.detail?.missing_fields;
    throw err;
  }
  return data;
};

export const apiGetPurchaseHistory = async () => {
  const res = await fetch(`${SO_URL}/treasury/purchases/${APP_ID}`, { credentials: 'include', headers: authHeader() });
  if (!res.ok) return { purchases: [] };
  return res.json();
};

export const apiDownloadInvoice = async (receiptId) => {
  const res = await fetch(`${SO_URL}/purchase/${receiptId}/invoice`, { credentials: 'include', headers: authHeader() });
  if (!res.ok) throw new Error('Invoice download failed');
  return res.blob();
};

export const apiDownloadReceipt = async (receiptId) => {
  const res = await fetch(`${SO_URL}/purchase/${receiptId}/receipt`, { credentials: 'include', headers: authHeader() });
  if (!res.ok) throw new Error('Receipt download failed');
  return res.blob();
};

export const apiChangePassword = async (new_password, confirm_password) => {
  const res = await fetch(`${SO_URL}/auth/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_password, confirm_password, app_id: APP_ID }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Password update failed');
  return data;
};

export const apiDeleteAccount = async (reason, reason_text) => {
  const res = await fetch(`${SO_URL}/auth/account`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, reason_text }),
  });
  const data = await res.json();
  if (!res.ok) {
    const parsed = _parseErrorDetail(data, 'Account deletion failed');
    throw new Error(parsed.message);
  }
  return data;
};

export const apiLookupPincode = async (pincode) => {
  const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  const data = await res.json();
  const office = data?.[0]?.PostOffice?.[0];
  if (!office) return null;
  return { district: office.District, state: office.State };
};

// ── Analytics Report Routes ───────────────────────────────────────────────────────────

export const apiPingTraffic = async () => {
  try {
    await fetch(`${SO_URL}/telemetry/visit/APP_CAAPP_20260508_224158_4252`, {
      method: 'POST',
    });
  } catch { /* silent — never block page load on this */ }
};